import { useState, useEffect, useCallback, Fragment } from 'react';
import {
  Container,
  Stack,
  Title,
  Paper,
  Button,
  Group,
  Alert,
  Text,
  Badge,
  Table,
  ActionIcon,
  Tooltip,
  Select,
  Pagination,
  TextInput,
  Switch,
  Timeline,
  Collapse,
  Skeleton,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconUserCheck,
  IconTrash,
  IconRefresh,
  IconDatabase,
  IconCheck,
  IconX,
  IconCopy,
  IconChevronRight,
  IconChevronDown,
  IconChevronUp,
  IconMail,
  IconSearch,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { useDebouncedValue, useMediaQuery } from '@mantine/hooks';
import { useAppUser } from '../../hooks/useAppUser';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useAdminContext } from './AdminLayout';
import { SUBSCRIPTION_BADGES, WRAP_BADGE_STYLES } from './adminUserShared';

type UserListItem = {
  uid: string;
  email?: string;
  displayName?: string;
  createdAt: string;
  // Activity / plan timestamps (ISO strings). Declared explicitly so named
  // access stays `string` despite the dynamic index signature below.
  lastActiveAt?: string;
  lastPlanCreatedAt?: string;
  lastPlanActivatedAt?: string;
  lastSwapAt?: string;
  // Activation-signal counters (lifetime, denormalized on the user doc).
  mealPlanCount?: number;
  mealSwapCount?: number;
  // Onboarding progress: set true when the wizard finishes; onboardingStep is
  // the last step index the wizard saved (0-based) for users who bailed.
  completedOnboarding?: boolean;
  onboardingStep?: number;
  // Whether the user is opted out of lifecycle/drip emails, and when.
  lifecycleEmailsOptOut?: boolean;
  lifecycleEmailsOptOutAt?: string;
  // Per-user feature flag overrides (absent key = follows the global flag)
  featureFlags?: Record<string, boolean | undefined>;
  // Billing state, so a delete can warn that it cancels a paying subscription.
  subscription?: {
    status?: string;
    planType?: string;
    stripeSubscriptionId?: string;
    // Set when a trialing user asked to cancel. The reason is the useful part —
    // it's the only exit-interview answer we get from a trial that never
    // converted.
    trialCanceledAt?: string;
    trialCancelReason?: string;
  };
  // Lifecycle email "sent at" timestamps (ISO strings), looked up dynamically by
  // field name for the per-user activity timeline. Present only if that email fired.
  [key: string]: unknown;
};

// Ordered lifecycle/transactional emails, with the user-doc field that records
// when each was sent. Drives the expandable email timeline. Keep in sync with
// the *EmailSentAt fields serialized by the backend.
const EMAIL_TIMELINE_META: { field: string; label: string }[] = [
  { field: 'welcomeEmailSentAt', label: 'Welcome' },
  { field: 'finishOnboardingEmailSentAt', label: 'Finish onboarding' },
  { field: 'firstPlanEmailSentAt', label: 'First plan' },
  { field: 'makeItYoursEmailSentAt', label: 'Make it yours' },
  { field: 'nutritionSpotlightEmailSentAt', label: 'Nutrition spotlight' },
  { field: 'grocerySpotlightEmailSentAt', label: 'Grocery spotlight' },
  { field: 'returnForPlan2EmailSentAt', label: 'Return for plan 2' },
  { field: 'trialEndingEmailSentAt', label: 'Trial ending' },
  { field: 'feedbackCheckinEmailSentAt', label: 'Feedback check-in' },
  { field: 'winBackEmailSentAt', label: 'Win-back' },
  { field: 'cancellationEmailSentAt', label: 'Cancellation' },
  { field: 'trialCanceledEmailSentAt', label: 'Trial canceled' },
  { field: 'subscriptionThanksEmailSentAt', label: 'Subscription thanks' },
];

type PaginatedUsersResult = {
  users: UserListItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
};

type UserSortField =
  | 'createdAt'
  | 'lastActiveAt'
  | 'lastPlanCreatedAt'
  | 'lastPlanActivatedAt'
  | 'lastSwapAt'
  | 'mealPlanCount'
  | 'mealSwapCount';

const PLAN_FILTER_OPTIONS = [
  { value: 'created_plan', label: 'Created a plan' },
  { value: 'activated_plan', label: 'Activated a plan' },
  { value: 'created_not_activated', label: 'Created, never activated' },
  { value: 'no_plan', label: 'No plan yet' },
];

const SUBSCRIPTION_FILTER_OPTIONS = [
  { value: 'trial', label: 'Trial' },
  { value: 'active', label: 'Active' },
  { value: 'past_due', label: 'Past due' },
  { value: 'canceled', label: 'Canceled' },
];

const ACTIVITY_FILTER_OPTIONS = [
  { value: '7', label: 'Active in last 7 days' },
  { value: '30', label: 'Active in last 30 days' },
  { value: '90', label: 'Active in last 90 days' },
];

// The two strongest activation signals: repeat plan creation and swaps.
const MIN_PLANS_FILTER_OPTIONS = [
  { value: '2', label: '2+ plans created' },
  { value: '3', label: '3+ plans created' },
  { value: '5', label: '5+ plans created' },
];

const SWAP_FILTER_OPTIONS = [
  { value: 'has_swapped', label: 'Has swapped meals' },
  { value: 'no_swaps', label: 'Never swapped' },
];

const ONBOARDING_FILTER_OPTIONS = [
  { value: 'completed', label: 'Completed onboarding' },
  { value: 'incomplete', label: "Didn't finish onboarding" },
];

// Clickable column header that shows the active sort direction. `miw`
// reserves width for columns whose cells hold badges: badges are the one
// thing the table's auto-layout can squeeze to nothing (their label
// ellipsizes), so without a minimum the wide text columns crush them into
// "..." even on desktop.
const SortableTh = ({
  label,
  field,
  sortField,
  sortDirection,
  onSort,
  miw,
}: {
  label: string;
  field: UserSortField;
  sortField: UserSortField;
  sortDirection: 'asc' | 'desc';
  onSort: (field: UserSortField) => void;
  miw?: number;
}) => (
  <Table.Th
    onClick={() => onSort(field)}
    miw={miw}
    style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
  >
    <Group gap={4} wrap="nowrap">
      {label}
      {sortField === field &&
        (sortDirection === 'desc' ? (
          <IconChevronDown size="0.8rem" />
        ) : (
          <IconChevronUp size="0.8rem" />
        ))}
    </Group>
  </Table.Th>
);

// Confirmation body for the irreversible user delete. The admin has to type
// the target's email (or their uid, when the profile has no email) before the
// delete button unlocks.
const DeleteUserConfirmContent = ({
  target,
  onConfirm,
}: {
  target: UserListItem;
  onConfirm: () => void;
}) => {
  const [typed, setTyped] = useState('');
  const expected = target.email || target.uid;
  const hasLiveSubscription =
    !!target.subscription?.stripeSubscriptionId &&
    target.subscription?.status !== 'canceled';

  return (
    <Stack gap="md">
      <Text size="sm">
        This permanently deletes{' '}
        <strong>{target.displayName || expected}</strong> along with every meal
        plan, recipe, pantry item, and preference they own, plus their sign-in
        account. It cannot be undone.
      </Text>
      {hasLiveSubscription && (
        <Alert color="orange" icon={<IconAlertCircle size="1rem" />} radius="md">
          <Text size="sm">
            This user has a {target.subscription?.planType || ''} subscription
            {target.subscription?.status === 'active' ? '' : ` (${target.subscription?.status})`}.
            Deleting them cancels it in Stripe immediately, with no refund for
            the current period.
          </Text>
        </Alert>
      )}
      <TextInput
        label={`Type ${expected} to confirm`}
        placeholder={expected}
        value={typed}
        onChange={(e) => setTyped(e.currentTarget.value)}
        autoComplete="off"
        data-autofocus
      />
      <Group justify="flex-end">
        <Button variant="default" onClick={() => modals.closeAll()}>
          Cancel
        </Button>
        <Button
          color="red"
          leftSection={<IconTrash size="1rem" />}
          disabled={typed.trim() !== expected}
          onClick={() => {
            modals.closeAll();
            onConfirm();
          }}
        >
          Delete permanently
        </Button>
      </Group>
    </Stack>
  );
};

export const AdminUsersPage = () => {
  usePageTitle('Admin Users | Meals You Love');
  const { user } = useAppUser();
  const { impersonationStatus } = useAdminContext();
  const [impersonating, setImpersonating] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersData, setUsersData] = useState<PaginatedUsersResult | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [userSearch, setUserSearch] = useState('');
  const [debouncedUserSearch] = useDebouncedValue(userSearch, 300);
  const [planFilter, setPlanFilter] = useState<string | null>(null);
  const [subscriptionFilter, setSubscriptionFilter] = useState<string | null>(null);
  const [activityFilter, setActivityFilter] = useState<string | null>(null);
  const [minPlansFilter, setMinPlansFilter] = useState<string | null>(null);
  const [swapFilter, setSwapFilter] = useState<string | null>(null);
  const [onboardingFilter, setOnboardingFilter] = useState<string | null>(null);
  const [sortField, setSortField] = useState<UserSortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [deletingUser, setDeletingUser] = useState<string | null>(null);
  const [optOutUpdating, setOptOutUpdating] = useState<string | null>(null);
  // Feature flag controls: global values give the switch its default; the
  // per-user override is what the switch writes
  const [globalFlags, setGlobalFlags] = useState<Record<string, boolean | undefined>>({});
  const [userFlagUpdating, setUserFlagUpdating] = useState<string | null>(null);

  // Phones can't fit the default nine pagination controls on one row, so the
  // user pagination drops its sibling pages instead of wrapping onto a second.
  const isMobile = useMediaQuery('(max-width: 768px)');

  const loadUsers = useCallback(async (page: number, search: string) => {
    if (!user) return;

    setUsersLoading(true);
    try {
      const token = await user.getIdToken();
      const params = new URLSearchParams({
        page: String(page),
        pageSize: '20',
        sort: `${sortField}:${sortDirection}`,
      });
      if (search.trim()) {
        params.set('search', search.trim());
      }
      if (planFilter) {
        params.set('planFilter', planFilter);
      }
      if (subscriptionFilter) {
        params.set('subscriptionStatus', subscriptionFilter);
      }
      if (activityFilter) {
        params.set('activeWithinDays', activityFilter);
      }
      if (minPlansFilter) {
        params.set('minPlans', minPlansFilter);
      }
      if (swapFilter) {
        params.set('swapFilter', swapFilter);
      }
      if (onboardingFilter) {
        params.set('onboardingFilter', onboardingFilter);
      }
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/users?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUsersData(data);
      } else {
        notifications.show({
          title: 'Error',
          message: 'Failed to load users',
          color: 'red',
        });
      }
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to load users',
        color: 'red',
      });
    } finally {
      setUsersLoading(false);
    }
  }, [user, planFilter, subscriptionFilter, activityFilter, minPlansFilter, swapFilter, onboardingFilter, sortField, sortDirection]);

  // Sorting: clicking the active column flips direction; a new column starts
  // at descending (most recent first), which is what "who was active last"
  // questions want.
  const handleSort = (field: UserSortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleImpersonateUser = async (targetUserId: string) => {
    if (!user) return;

    setImpersonating(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/impersonate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({ userId: targetUserId }),
      });

      if (response.ok) {
        // Small delay to ensure cookie is set before reload
        setTimeout(() => {
          window.location.reload();
        }, 100);
      } else {
        const error = await response.json();
        notifications.show({
          title: 'Error',
          message: error.error || 'Failed to start impersonation',
          color: 'red',
        });
        setImpersonating(false);
      }
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to start impersonation',
        color: 'red',
      });
      setImpersonating(false);
    }
  };

  const handleDeleteUser = async (target: UserListItem) => {
    if (!user) return;

    setDeletingUser(target.uid);
    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/users/${target.uid}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        notifications.show({
          title: 'User deleted',
          message: `${target.email || target.uid} and all of their data were removed${
            result.stripeSubscriptionCanceled
              ? '. Their Stripe subscription was canceled.'
              : ''
          }`,
          color: 'green',
          icon: <IconCheck size={14} />,
        });
        setExpandedUsers((prev) => {
          const next = new Set(prev);
          next.delete(target.uid);
          return next;
        });
        // Deleting the only row on a trailing page would leave the table empty,
        // so step back a page and let the load effect refetch.
        if (usersData?.users.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          loadUsers(currentPage, debouncedUserSearch);
        }
      } else {
        const error = await response.json();
        notifications.show({
          title: 'Error',
          message: error.error || 'Failed to delete user',
          color: 'red',
        });
      }
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to delete user',
        color: 'red',
      });
    } finally {
      setDeletingUser(null);
    }
  };

  const confirmDeleteUser = (target: UserListItem) => {
    modals.open({
      title: 'Delete this user?',
      children: (
        <DeleteUserConfirmContent
          target={target}
          onConfirm={() => handleDeleteUser(target)}
        />
      ),
    });
  };

  const copyUserId = (uid: string) => {
    navigator.clipboard.writeText(uid);
    notifications.show({
      message: 'User ID copied',
      color: 'green',
      icon: <IconCheck size={14} />,
      autoClose: 2000,
    });
  };

  const toggleUserExpanded = (uid: string) => {
    setExpandedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) {
        next.delete(uid);
      } else {
        next.add(uid);
      }
      return next;
    });
  };

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const token = await user.getIdToken();
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/feature-flags/global`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setGlobalFlags(data.featureFlags || {});
        }
      } catch (error) {
        console.error('Failed to load global feature flags:', error);
      }
    })();
  }, [user]);

  const handleSetUserFlag = async (uid: string, flag: string, enabled: boolean) => {
    if (!user) return;
    setUserFlagUpdating(uid);
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/feature-flags/user/${uid}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ featureFlags: { [flag]: enabled } }),
      });
      if (!response.ok) throw new Error('Request failed');
      setUsersData(prev =>
        prev
          ? {
              ...prev,
              users: prev.users.map(u =>
                u.uid === uid
                  ? { ...u, featureFlags: { ...u.featureFlags, [flag]: enabled } }
                  : u
              ),
            }
          : prev
      );
      notifications.show({
        message: `${flag} ${enabled ? 'enabled' : 'disabled'} for user`,
        color: enabled ? 'green' : 'orange',
        autoClose: 3000,
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: `Failed to update flag ${flag} for user`,
        color: 'red',
      });
    } finally {
      setUserFlagUpdating(null);
    }
  };

  const handleSetLifecycleOptOut = async (uid: string, optOut: boolean) => {
    if (!user) return;

    setOptOutUpdating(uid);
    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/users/${uid}/lifecycle-opt-out`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ optOut }),
        }
      );

      if (response.ok) {
        notifications.show({
          message: optOut
            ? 'User unsubscribed from lifecycle emails'
            : 'User re-subscribed to lifecycle emails',
          color: 'green',
          icon: <IconCheck size={14} />,
          autoClose: 2500,
        });
        loadUsers(currentPage, debouncedUserSearch);
      } else {
        const error = await response.json();
        notifications.show({
          title: 'Error',
          message: error.error || 'Failed to update lifecycle opt-out',
          color: 'red',
        });
      }
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to update lifecycle opt-out',
        color: 'red',
      });
    } finally {
      setOptOutUpdating(null);
    }
  };

  // Build the ordered, sent-only email timeline for a user from the serialized
  // *EmailSentAt fields, sorted chronologically.
  const buildEmailTimeline = (listUser: UserListItem) =>
    EMAIL_TIMELINE_META.map(({ field, label }) => {
      const sentAt = listUser[field];
      return typeof sentAt === 'string'
        ? { label, sentAt: new Date(sentAt) }
        : null;
    })
      .filter((item): item is { label: string; sentAt: Date } => item !== null)
      .sort((a, b) => a.sentAt.getTime() - b.sentAt.getTime());

  // A new search term, filter, or sort always starts at page 1, otherwise a
  // page number from the previous query can land past the end of the filtered
  // results.
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedUserSearch, planFilter, subscriptionFilter, activityFilter, minPlansFilter, swapFilter, onboardingFilter, sortField, sortDirection]);

  useEffect(() => {
    if (!user?.isAdmin) return;
    loadUsers(currentPage, debouncedUserSearch);
  }, [user, loadUsers, currentPage, debouncedUserSearch]);

  if (!user) {
    return null;
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Paper withBorder p="md" radius="lg">
          <Stack gap="md">
            <Group justify="space-between" align="center">
              <div>
                <Title order={3}>All Users</Title>
                <Text size="sm" c="dimmed">
                  {usersData
                    ? debouncedUserSearch.trim() || planFilter || subscriptionFilter || activityFilter || minPlansFilter || swapFilter || onboardingFilter
                      ? `${usersData.total} matching ${usersData.total === 1 ? 'user' : 'users'}`
                      : `${usersData.total} total users`
                    : 'Loading users...'}
                </Text>
              </div>
              <Button
                leftSection={<IconRefresh size="1rem" />}
                variant="outline"
                onClick={() => loadUsers(currentPage, debouncedUserSearch)}
                loading={usersLoading}
              >
                Refresh
              </Button>
            </Group>

            <TextInput
              placeholder="Search by name, email, or user ID"
              value={userSearch}
              onChange={(e) => setUserSearch(e.currentTarget.value)}
              leftSection={<IconSearch size="1rem" />}
              rightSection={
                userSearch ? (
                  <Tooltip label="Clear search">
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      onClick={() => setUserSearch('')}
                    >
                      <IconX size="0.8rem" />
                    </ActionIcon>
                  </Tooltip>
                ) : null
              }
            />

            {/* flex-basis instead of fixed widths so the filters reflow into
                full-width rows on phones instead of overflowing. */}
            <Group gap="sm">
              <Select
                placeholder="Meal plan status"
                data={PLAN_FILTER_OPTIONS}
                value={planFilter}
                onChange={setPlanFilter}
                clearable
                style={{ flex: '1 1 200px' }}
              />
              <Select
                placeholder="Subscription"
                data={SUBSCRIPTION_FILTER_OPTIONS}
                value={subscriptionFilter}
                onChange={setSubscriptionFilter}
                clearable
                style={{ flex: '1 1 140px' }}
              />
              <Select
                placeholder="Last active"
                data={ACTIVITY_FILTER_OPTIONS}
                value={activityFilter}
                onChange={setActivityFilter}
                clearable
                style={{ flex: '1 1 180px' }}
              />
              <Select
                placeholder="Plans created"
                data={MIN_PLANS_FILTER_OPTIONS}
                value={minPlansFilter}
                onChange={setMinPlansFilter}
                clearable
                style={{ flex: '1 1 150px' }}
              />
              <Select
                placeholder="Swap activity"
                data={SWAP_FILTER_OPTIONS}
                value={swapFilter}
                onChange={setSwapFilter}
                clearable
                style={{ flex: '1 1 160px' }}
              />
              <Select
                placeholder="Onboarding"
                data={ONBOARDING_FILTER_OPTIONS}
                value={onboardingFilter}
                onChange={setOnboardingFilter}
                clearable
                style={{ flex: '1 1 190px' }}
              />
            </Group>

            {!usersData && usersLoading ? (
              // First load: table-row-shaped skeletons, matching the other
              // admin pages' loading states. Refreshes keep the previous
              // table and spin the Refresh button instead.
              <Stack gap="xs">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} height={44} radius="sm" />
                ))}
              </Stack>
            ) : usersData && usersData.users.length > 0 ? (
              <>
                {/* minWidth keeps the 11 columns readable and scrolls
                    horizontally on narrow screens instead of crushing cells;
                    sized to fit the badge columns' reserved minimums. */}
                <Table.ScrollContainer minWidth={1270}>
                  <Table>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th w={40} />
                        <Table.Th>Display Name</Table.Th>
                        <Table.Th>Email</Table.Th>
                        {/* miw so the badge cells can't be squeezed into
                            "..." — see SortableTh */}
                        <Table.Th miw={110}>Subscription</Table.Th>
                        <Table.Th miw={110}>Onboarding</Table.Th>
                        <SortableTh
                          label="Meal Plan"
                          field="lastPlanCreatedAt"
                          sortField={sortField}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                          miw={110}
                        />
                        <SortableTh
                          label="Plans"
                          field="mealPlanCount"
                          sortField={sortField}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                        />
                        <SortableTh
                          label="Swaps"
                          field="mealSwapCount"
                          sortField={sortField}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                        />
                        <SortableTh
                          label="Created At"
                          field="createdAt"
                          sortField={sortField}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                        />
                        <SortableTh
                          label="Last Active"
                          field="lastActiveAt"
                          sortField={sortField}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                        />
                        <Table.Th>Actions</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {usersData.users.map((listUser) => {
                        const isExpanded = expandedUsers.has(listUser.uid);
                        const timeline = buildEmailTimeline(listUser);
                        return (
                          <Fragment key={listUser.uid}>
                            <Table.Tr
                              style={{ cursor: 'pointer' }}
                              onClick={() => toggleUserExpanded(listUser.uid)}
                            >
                              <Table.Td>
                                <Tooltip label={isExpanded ? 'Hide email timeline' : 'Show email timeline'}>
                                  <ActionIcon variant="subtle" color="gray" size="sm">
                                    {isExpanded ? <IconChevronDown size="1rem" /> : <IconChevronRight size="1rem" />}
                                  </ActionIcon>
                                </Tooltip>
                              </Table.Td>
                              <Table.Td>
                                <Text fw={500}>{listUser.displayName || '-'}</Text>
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm">{listUser.email || '-'}</Text>
                              </Table.Td>
                              <Table.Td>
                                {listUser.subscription?.planType === 'lifetime' ? (
                                  <Badge color="violet" variant="light">
                                    Lifetime
                                  </Badge>
                                ) : listUser.subscription?.status &&
                                  SUBSCRIPTION_BADGES[listUser.subscription.status] ? (
                                  <Badge
                                    color={SUBSCRIPTION_BADGES[listUser.subscription.status].color}
                                    variant="light"
                                  >
                                    {SUBSCRIPTION_BADGES[listUser.subscription.status].label}
                                  </Badge>
                                ) : (
                                  <Text size="sm" c="dimmed">
                                    -
                                  </Text>
                                )}
                              </Table.Td>
                              <Table.Td>
                                {listUser.completedOnboarding ? (
                                  <Badge color="green" variant="light">
                                    Complete
                                  </Badge>
                                ) : (
                                  <Tooltip
                                    label={
                                      typeof listUser.onboardingStep === 'number'
                                        ? `Last saved step ${listUser.onboardingStep + 1}`
                                        : 'Never advanced past the first step'
                                    }
                                  >
                                    <Badge color="yellow" variant="light">
                                      Incomplete
                                    </Badge>
                                  </Tooltip>
                                )}
                              </Table.Td>
                              <Table.Td>
                                {listUser.lastPlanActivatedAt ? (
                                  <Tooltip
                                    label={`Last activated ${new Date(listUser.lastPlanActivatedAt).toLocaleDateString()}`}
                                  >
                                    <Badge color="green" variant="light">
                                      Activated
                                    </Badge>
                                  </Tooltip>
                                ) : listUser.lastPlanCreatedAt ? (
                                  <Tooltip
                                    label={`Last created ${new Date(listUser.lastPlanCreatedAt).toLocaleDateString()}, never activated`}
                                  >
                                    <Badge color="yellow" variant="light">
                                      Created
                                    </Badge>
                                  </Tooltip>
                                ) : (
                                  <Badge color="gray" variant="light">
                                    None
                                  </Badge>
                                )}
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm" c={listUser.mealPlanCount ? undefined : 'dimmed'}>
                                  {listUser.mealPlanCount ?? 0}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                {listUser.lastSwapAt ? (
                                  <Tooltip
                                    label={`Last swap ${new Date(listUser.lastSwapAt).toLocaleString()}`}
                                  >
                                    <Text size="sm">{listUser.mealSwapCount ?? 0}</Text>
                                  </Tooltip>
                                ) : (
                                  <Text size="sm" c="dimmed">
                                    {listUser.mealSwapCount ?? 0}
                                  </Text>
                                )}
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm">
                                  {new Date(listUser.createdAt).toLocaleDateString()}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm" c={listUser.lastActiveAt ? undefined : 'dimmed'}>
                                  {listUser.lastActiveAt
                                    ? new Date(listUser.lastActiveAt).toLocaleString()
                                    : 'Never'}
                                </Text>
                              </Table.Td>
                              <Table.Td onClick={(e) => e.stopPropagation()}>
                                <Group gap="xs" wrap="nowrap">
                                  <Tooltip label="Copy user ID">
                                    <ActionIcon
                                      size="sm"
                                      variant="subtle"
                                      color="gray"
                                      onClick={() => copyUserId(listUser.uid)}
                                    >
                                      <IconCopy size="0.8rem" />
                                    </ActionIcon>
                                  </Tooltip>
                                  <Button
                                    size="xs"
                                    leftSection={<IconUserCheck size="0.8rem" />}
                                    onClick={() => handleImpersonateUser(listUser.uid)}
                                    disabled={impersonating || impersonationStatus?.isImpersonating || listUser.uid === user.uid}
                                    loading={impersonating}
                                  >
                                    Impersonate
                                  </Button>
                                  <Tooltip
                                    label={
                                      listUser.uid === user.uid
                                        ? 'You cannot delete your own account'
                                        : 'Delete user and all of their data'
                                    }
                                  >
                                    <ActionIcon
                                      size="sm"
                                      variant="subtle"
                                      color="red"
                                      disabled={
                                        listUser.uid === user.uid ||
                                        listUser.uid === impersonationStatus?.impersonatingUserId
                                      }
                                      loading={deletingUser === listUser.uid}
                                      onClick={() => confirmDeleteUser(listUser)}
                                    >
                                      <IconTrash size="0.8rem" />
                                    </ActionIcon>
                                  </Tooltip>
                                </Group>
                              </Table.Td>
                            </Table.Tr>
                            <Table.Tr>
                              <Table.Td colSpan={11} p={0} style={{ borderBottom: isExpanded ? undefined : 'none' }}>
                                <Collapse in={isExpanded}>
                                  {/* The table scrolls horizontally on
                                      phones; sticky-left pins the expanded
                                      detail to the visible viewport instead
                                      of stretching across the full 1100px
                                      row. */}
                                  <div
                                    style={{
                                      padding: '16px 24px',
                                      position: 'sticky',
                                      left: 0,
                                      maxWidth: 'min(100%, calc(100vw - 72px))',
                                    }}
                                  >
                                    <Group gap="xl" mb="lg">
                                      <div>
                                        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                                          Last plan generated
                                        </Text>
                                        <Text size="sm">
                                          {listUser.lastPlanCreatedAt
                                            ? new Date(listUser.lastPlanCreatedAt).toLocaleString()
                                            : 'Never'}
                                        </Text>
                                      </div>
                                      <div>
                                        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                                          Last plan activated
                                        </Text>
                                        <Text size="sm">
                                          {listUser.lastPlanActivatedAt
                                            ? new Date(listUser.lastPlanActivatedAt).toLocaleString()
                                            : 'Never'}
                                        </Text>
                                      </div>
                                      <div>
                                        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                                          Last meal swap
                                        </Text>
                                        <Text size="sm">
                                          {listUser.lastSwapAt
                                            ? new Date(listUser.lastSwapAt).toLocaleString()
                                            : 'Never'}
                                        </Text>
                                      </div>
                                    </Group>
                                    <Group gap="sm" align="center" mb="sm">
                                      <Switch
                                        checked={
                                          listUser.featureFlags?.['additional_meals'] ??
                                          !!globalFlags['additional_meals']
                                        }
                                        disabled={userFlagUpdating === listUser.uid}
                                        onChange={(e) =>
                                          handleSetUserFlag(
                                            listUser.uid,
                                            'additional_meals',
                                            e.currentTarget.checked
                                          )
                                        }
                                        label="Breakfast & lunch planning"
                                      />
                                      {listUser.featureFlags?.['additional_meals'] !== undefined && (
                                        <Badge color="violet" variant="light">
                                          Override:{' '}
                                          {listUser.featureFlags['additional_meals'] ? 'on' : 'off'}
                                        </Badge>
                                      )}
                                    </Group>
                                    <Group gap="sm" align="center" mb="lg">
                                      <Switch
                                        checked={!listUser.lifecycleEmailsOptOut}
                                        disabled={optOutUpdating === listUser.uid}
                                        onChange={(e) =>
                                          handleSetLifecycleOptOut(
                                            listUser.uid,
                                            !e.currentTarget.checked
                                          )
                                        }
                                        label="Subscribed to lifecycle emails"
                                      />
                                      {listUser.lifecycleEmailsOptOut && (
                                        <Badge
                                          color="gray"
                                          variant="light"
                                          styles={WRAP_BADGE_STYLES}
                                        >
                                          Unsubscribed
                                          {typeof listUser.lifecycleEmailsOptOutAt === 'string'
                                            ? ` · ${new Date(listUser.lifecycleEmailsOptOutAt).toLocaleDateString()}`
                                            : ''}
                                        </Badge>
                                      )}
                                      {listUser.subscription?.trialCanceledAt && (
                                        <Badge
                                          color="red"
                                          variant="light"
                                          styles={WRAP_BADGE_STYLES}
                                        >
                                          Trial canceled ·{' '}
                                          {new Date(
                                            listUser.subscription.trialCanceledAt
                                          ).toLocaleDateString()}
                                          {listUser.subscription.trialCancelReason
                                            ? ` · ${listUser.subscription.trialCancelReason}`
                                            : ''}
                                        </Badge>
                                      )}
                                    </Group>
                                    <Text size="sm" fw={500} mb="sm">
                                      Email timeline
                                    </Text>
                                    {timeline.length > 0 ? (
                                      <Timeline active={timeline.length - 1} bulletSize={20} lineWidth={2}>
                                        {timeline.map((item) => (
                                          <Timeline.Item
                                            key={item.label}
                                            bullet={<IconMail size="0.7rem" />}
                                            title={item.label}
                                          >
                                            <Text size="xs" c="dimmed">
                                              {item.sentAt.toLocaleString()}
                                            </Text>
                                          </Timeline.Item>
                                        ))}
                                      </Timeline>
                                    ) : (
                                      <Text size="sm" c="dimmed">
                                        No lifecycle emails sent yet.
                                      </Text>
                                    )}
                                  </div>
                                </Collapse>
                              </Table.Td>
                            </Table.Tr>
                          </Fragment>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>

                <Group justify="center">
                  <Pagination
                    total={usersData.totalPages}
                    value={currentPage}
                    onChange={setCurrentPage}
                    siblings={isMobile ? 0 : 1}
                    boundaries={1}
                    gap={isMobile ? 4 : 8}
                  />
                </Group>
              </>
            ) : (
              <Alert
                icon={<IconDatabase size="1rem" />}
                title="No Users"
                color="blue"
                radius="lg"
              >
                {usersLoading
                  ? 'Loading users...'
                  : planFilter || subscriptionFilter || activityFilter || minPlansFilter || swapFilter || onboardingFilter
                    ? 'No users match those filters.'
                    : debouncedUserSearch.trim()
                      ? 'No users match that search.'
                      : 'No users found.'}
              </Alert>
            )}
          </Stack>
        </Paper>

        <Alert
          icon={<IconAlertCircle size="1rem" />}
          title="Warning"
          color="yellow"
          radius="lg"
        >
          Use impersonation responsibly. You will have full access to the impersonated user's account and data.
        </Alert>
      </Stack>
    </Container>
  );
};
